import React, { useState } from 'react'
import './home.css'
import Header from '../../Components/Header/Header'
import { FaMagic, FaPenAlt, FaStar, FaFilm, FaScroll, FaBolt, FaTimes, FaCheck, FaChevronRight, FaFileCode } from 'react-icons/fa'
import { tryParseCompositionJson } from '../Composition/compositionImportUtils'
import { MdOutlineMovie, MdOutlineAspectRatio } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Loader from '../../Components/Loader/Loader'
import { useAuthContext } from '../../hooks/UseAuthContext'
import api from '../../api'
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox'

const NICHES = [
  { id: 'celebrity/politics', label: 'Celebrity & Politics', icon: '🎭' },
  { id: 'history',            label: 'History',             icon: '📜' },
  { id: 'philosophy',         label: 'Philosophy',          icon: '🧠' },
  { id: 'commentary',         label: 'Commentary',          icon: '🎙️' },
  { id: 'original',           label: 'Original',            icon: '✨' },
]

const PROMPT_TYPES = [
  { id: 'transcript', label: 'Transcript', icon: <FaScroll />, desc: 'Use an existing transcript' },
  { id: 'script',     label: 'Pure Script', icon: <FaFilm />,  desc: 'Generate from a script' },
  { id: 'composition_json', label: 'JSON Composer', icon: <FaFileCode />, desc: 'Import composition JSON (clips + timeline)' },
  { id: 'prompt',     label: 'Prompt',      icon: <FaBolt />,  desc: 'Coming soon' },
]

const DURATIONS = ['1', '4', '10', '15', '20']

const SPLIT_MODES = [
  { id: 'atomic',   label: 'Atomic',   desc: 'Short, precise cuts' },
  { id: 'natural',  label: 'Natural',  desc: 'Balanced pacing' },
  { id: 'extended', label: 'Extended', desc: 'Longer sequences' },
]

const COMMENTARY_MODES = [
  { id: 'paraphrase', label: 'Paraphrase', desc: 'Rewrite for commentary style' },
  { id: 'original',   label: 'Original',   desc: 'Keep script as-is' },
]

function CookieField({ label, required, value, onChange, index }) {
  return (
    <div className="cookie-field">
      <div className="cookie-field-header">
        <span className="cookie-badge">{index}</span>
        <span className="cookie-label">{label}</span>
        {required && <span className="cookie-required">Required</span>}
        {!required && <span className="cookie-optional">Optional</span>}
      </div>
      <textarea
        className="cookie-textarea"
        placeholder={`Paste Google cookie from Account ${index} (Netscape format)…`}
        value={value}
        onChange={onChange}
      />
      {value && <div className="cookie-char-count">{value.length} chars</div>}
    </div>
  )
}

function SelectButton({ active, onClick, children, disabled }) {
  return (
    <button
      className={`select-btn ${active ? 'select-btn--active' : ''} ${disabled ? 'select-btn--disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
    >
      {active && <FaCheck className="select-btn-check" />}
      {children}
    </button>
  )
}

function Home() {
  const [orientation, setOrientation]         = useState('landscape')
  const [promptType, setPromptType]         = useState('')
  const [submitted, setSubmitted]           = useState(false)
  const [duration, setDuration]             = useState('')
  const [emotion, setEmotion]               = useState('')
  const [loading, setLoading]               = useState(false)
  const [optionalPrompt, setOptionalPrompt] = useState('')
  const [vidMetadata, setVidMetaData]       = useState(false)
  const [prompt, setPrompt]                 = useState('')
  const [niche, setNiche]                   = useState('')
  const [splitMode, setSplitMode]           = useState('atomic')
  const [commentaryMode, setCommentaryMode] = useState('paraphrase')
  const [showConfirmation, setShowConfirmation] = useState(false)

  const [showCookieManager, setShowCookieManager] = useState(false)
  const [cookie1, setCookie1] = useState('')
  const [cookie2, setCookie2] = useState('')
  const [cookie3, setCookie3] = useState('')
  const [cookieLoading, setCookieLoading] = useState(false)

  const navigate = useNavigate()
  const { user } = useAuthContext()

  const handleCookieSubmit = () => {
    const cookieArray = [cookie1.trim(), cookie2.trim(), cookie3.trim()].filter(c => c !== '')
    if (cookieArray.length === 0) return toast.error('Please provide at least 1 cookie')
    if (cookieArray.some(c => c.length < 50)) return toast.error('One or more cookies look too short')

    setCookieLoading(true)
    api.post('/imagefx/cookies', { cookies: cookieArray }, {
      headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' }
    })
    .then(response => {
      toast.success(`${response.data.cookie_count} ImageFX cookie(s) updated successfully`)
      setCookie1(''); setCookie2(''); setCookie3('')
      setShowCookieManager(false)
    })
    .catch(err => toast.error(err.response?.data?.error || 'Failed to update cookies'))
    .finally(() => setCookieLoading(false))
  }

  const composeTranscript = () => {
    if (submitted || loading) return
    setSubmitted(true); setLoading(true)
    const resetState = () => { setLoading(false); setSubmitted(false) }

    if (!niche) { toast.error('You must select a niche'); return resetState() }

    const payload = {
      script: prompt,
      duration,
      emotion,
      niche,
      optionalPrompt,
      splitMode,
      orientation,
      ...(niche === 'commentary' && { commentaryMode }),
    }

    const goToCompose = (r) => {
      const id = r?.data?._id;
      if (!id) {
        toast.info(r?.data?.msg || 'Composition started — open it from Drafts when ready.');
        resetState();
        return;
      }
      resetState();
      navigate(`/compose/${id}`);
    };

    if (promptType === 'composition_json') {
      const parsed = tryParseCompositionJson(prompt)
      if (!parsed.ok) {
        toast.error(parsed.error || 'Invalid composition JSON')
        return resetState()
      }
      api.post(
        '/compose-composition-import',
        {
          ...payload,
          composition: parsed.composition,
        },
        { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' } },
      )
        .then(r => {
          if (parsed.warnings?.length) {
            parsed.warnings.forEach(w => toast.warn(w))
          }
          goToCompose(r)
        })
        .catch(err => {
          toast.error(err.response?.data?.error || 'Composition import failed')
          resetState()
        })
    } else if (promptType === 'transcript') {
      api.post('/compose-transcript-video', payload, { headers: { Authorization: `Bearer ${user.token}` } })
        .then(goToCompose)
        .catch(() => resetState())
    } else if (promptType === 'script') {
      if (!duration) { toast.error('Choose an approximate video duration'); return resetState() }
      api.post('/generate-video', payload, { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' } })
        .then(goToCompose)
        .catch(() => resetState())
    } else if (promptType === '') {
      toast.error('Choose a prompt type'); resetState()
    } else {
      toast.info(`${promptType} is coming soon.`); resetState()
    }
  }

  const compositionPreview = tryParseCompositionJson(prompt)

  const handleContinueClick = () => {
    if (compositionPreview.ok) {
      setVidMetaData(true)
      setPromptType('composition_json')
      return
    }
    if (!prompt || prompt.length < 500) {
      toast.error('Prompt is too short (min 500 characters), or paste valid composition JSON (schemaVersion 1).')
    } else {
      setVidMetaData(true)
    }
  }

  const handleConfirm = (confirmed) => {
    if (!confirmed || loading || submitted) return
    setShowConfirmation(false)
    composeTranscript()
  }

  const charCount = prompt.length
  const charProgress = compositionPreview.ok
    ? 100
    : Math.min((charCount / 500) * 100, 100)
  const charReady = compositionPreview.ok || charCount >= 500

  return (
    <div className="home">
      <Header />

      <div className="topbar">
        <div className="topbar-title">
          <MdOutlineMovie className="topbar-icon" />
          <span>Video Studio</span>
        </div>
        <button
          className={`cookie-btn ${showCookieManager ? 'cookie-btn--open' : ''}`}
          onClick={() => setShowCookieManager(!showCookieManager)}
        >
          🍪 <span>Manage Cookies</span>
          {showCookieManager ? <FaTimes className="cookie-btn-icon" /> : <FaChevronRight className="cookie-btn-icon" />}
        </button>
      </div>

      <div className={`cookie-drawer ${showCookieManager ? 'cookie-drawer--open' : ''}`}>
        <div className="cookie-drawer-inner">
          <div className="cookie-drawer-header">
            <h3>🍪 ImageFX Cookie Manager</h3>
            <p>Add cookies from up to 3 Google accounts for rotation and rate-limit avoidance.</p>
          </div>
          <div className="cookie-fields">
            <CookieField label="Account 1" required index={1} value={cookie1} onChange={e => setCookie1(e.target.value)} />
            <CookieField label="Account 2" index={2} value={cookie2} onChange={e => setCookie2(e.target.value)} />
            <CookieField label="Account 3" index={3} value={cookie3} onChange={e => setCookie3(e.target.value)} />
          </div>
          <div className="cookie-actions">
            <button className="btn-ghost" onClick={() => setShowCookieManager(false)}>Cancel</button>
            <button className="btn-primary" onClick={handleCookieSubmit} disabled={cookieLoading}>
              {cookieLoading ? <><span className="spinner" /> Saving…</> : <><FaCheck /> Save Cookies</>}
            </button>
          </div>
        </div>
      </div>

      {!vidMetadata && (
        <div className="prompt-stage">
          <div className="prompt-stage-label">
            <FaMagic className="stage-icon" />
            <span>Step 1 — Enter your content</span>
          </div>

          <div className="prompt-card">
            <textarea
              className="prompt-textarea"
              placeholder="Paste your script, transcript, or idea here…&#10;&#10;Or paste composition import JSON (schemaVersion 1).&#10;Otherwise minimum 500 characters to proceed."
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
            />

            <div className="prompt-footer">
              <div className="char-meter-wrap">
                <div className="char-meter">
                  <div
                    className={`char-meter-fill ${charReady ? 'char-meter-fill--ready' : ''}`}
                    style={{ width: `${charProgress}%` }}
                  />
                </div>
                <span className={`char-count ${charReady ? 'char-count--ready' : ''}`}>
                  {compositionPreview.ok ? 'Valid composition JSON ✓' : `${charCount} / 500${charReady ? ' ✓' : ''}`}
                </span>
              </div>

              <button
                className={`btn-proceed ${charReady ? 'btn-proceed--ready' : ''}`}
                onClick={handleContinueClick}
              >
                <FaStar /> Continue
                <FaChevronRight className="proceed-arrow" />
              </button>
            </div>
          </div>
        </div>
      )}

      {vidMetadata && (
        <div className="meta-stage">
          <div className="meta-stage-header">
            <div className="meta-stage-label">
              <MdOutlineAspectRatio className="stage-icon" />
              <span>Step 2 — Configure your video</span>
            </div>
            <button className="btn-back" onClick={() => setVidMetaData(false)}>
              <FaPenAlt /> Edit Prompt
            </button>
          </div>

          <div className="meta-grid">

            <div className="meta-section">
              <h4 className="meta-section-title">Prompt Type</h4>
              <div className="prompt-type-grid">
                {PROMPT_TYPES.map(pt => (
                  <button
                    key={pt.id}
                    className={`prompt-type-card ${promptType === pt.id ? 'prompt-type-card--active' : ''} ${pt.id === 'prompt' ? 'prompt-type-card--soon' : ''}`}
                    onClick={() => setPromptType(pt.id)}
                  >
                    <span className="pt-icon">{pt.icon}</span>
                    <span className="pt-label">{pt.label}</span>
                    <span className="pt-desc">{pt.desc}</span>
                    {pt.id === 'prompt' && <span className="soon-badge">Soon</span>}
                    {promptType === pt.id && <FaCheck className="pt-check" />}
                  </button>
                ))}
              </div>
            </div>

            {promptType !== 'composition_json' && (
            <div className="meta-section">
              <h4 className="meta-section-title">Duration</h4>
              <div className="pill-group">
                {DURATIONS.map(val => (
                  <SelectButton key={val} active={duration === val} onClick={() => setDuration(val)}>
                    {val} min
                  </SelectButton>
                ))}
              </div>
            </div>
            )}

            <div className="meta-section meta-section--wide">
              <h4 className="meta-section-title">Niche</h4>
              <div className="niche-grid">
                {NICHES.map(n => (
                  <button
                    key={n.id}
                    className={`niche-card ${niche === n.id ? 'niche-card--active' : ''}`}
                    onClick={() => {
                      setNiche(n.id)
                      if (n.id !== 'commentary') setCommentaryMode('paraphrase')
                    }}
                  >
                    <span className="niche-icon">{n.icon}</span>
                    <span className="niche-label">{n.label}</span>
                    {niche === n.id && <FaCheck className="niche-check" />}
                  </button>
                ))}
              </div>
            </div>

            {niche === 'commentary' && (
              <div className="meta-section">
                <h4 className="meta-section-title">Commentary Mode</h4>
                <div className="split-grid">
                  {COMMENTARY_MODES.map(cm => (
                    <button
                      key={cm.id}
                      className={`split-card ${commentaryMode === cm.id ? 'split-card--active' : ''}`}
                      onClick={() => setCommentaryMode(cm.id)}
                    >
                      <span className="split-label">{cm.label}</span>
                      <span className="split-desc">{cm.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {niche === 'original' && (
              <div className="meta-section">
                <h4 className="meta-section-title">Split Mode</h4>
                <div className="split-grid">
                  {SPLIT_MODES.map(sm => (
                    <button
                      key={sm.id}
                      className={`split-card ${splitMode === sm.id ? 'split-card--active' : ''}`}
                      onClick={() => setSplitMode(sm.id)}
                    >
                      <span className="split-label">{sm.label}</span>
                      <span className="split-desc">{sm.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="meta-section">
              <h4 className="meta-section-title">Orientation</h4>
              <div className="orientation-group">
                <button
                  className={`orientation-btn ${orientation === 'landscape' ? 'orientation-btn--active' : ''}`}
                  onClick={() => setOrientation('landscape')}
                >
                  <span className="orientation-icon orientation-icon--landscape">⬜</span>
                  <span>Landscape</span>
                  <span className="orientation-ratio">16:9</span>
                </button>
                <button
                  className={`orientation-btn ${orientation === 'portrait' ? 'orientation-btn--active' : ''}`}
                  onClick={() => setOrientation('portrait')}
                >
                  <span className="orientation-icon orientation-icon--portrait">▯</span>
                  <span>Portrait</span>
                  <span className="orientation-ratio">9:16</span>
                </button>
              </div>
            </div>

            <div className="meta-section">
              <h4 className="meta-section-title">Focus Emotion <span className="optional-tag">optional</span></h4>
              <input
                className="emotion-input"
                type="text"
                placeholder="e.g. Suspense, Wonder, Urgency…"
                value={emotion}
                onChange={e => setEmotion(e.target.value)}
              />
            </div>

          </div>

          <div className="compose-bar">
            <div className="compose-summary">
              {promptType && <span className="summary-chip">{promptType}</span>}
              {duration && <span className="summary-chip">{duration} min</span>}
              {niche && <span className="summary-chip">{niche}</span>}
              {niche === 'commentary' && <span className="summary-chip">{commentaryMode}</span>}
              {orientation && <span className="summary-chip">{orientation}</span>}
            </div>
            <button className="btn-compose" onClick={() => setShowConfirmation(true)}>
              <FaStar /> {promptType === 'composition_json' ? 'Compose from JSON' : 'Compose Script'}
            </button>
          </div>
        </div>
      )}

      {showConfirmation && (
        <ConfirmationBox
          message={promptType === 'composition_json'
            ? 'Import this composition JSON into the editor?'
            : 'Are you sure you want to compose this script?'}
          handleConfirm={handleConfirm}
        />
      )}

      {(loading || cookieLoading) && <Loader />}
    </div>
  )
}

export default Home